import { c as _c } from "react/compiler-runtime";
import type { OidcConfiguration } from '@axa-fr/react-oidc';
import { OidcProvider, OidcSecure, OidcUserStatus, useOidc, useOidcAccessToken, useOidcIdToken, useOidcUser } from '@axa-fr/react-oidc';
import { browserLogger } from '@newfront-insurance/dd-rum';
import type { DecodedIdentityJwt, DecodedJwt } from '@newfront-insurance/next-auth-api-types';
import type { Provider } from '@newfront-insurance/react-provision';
import { createProvider, useProvider } from '@newfront-insurance/react-provision';
import { useRouter } from 'next/router';
import { WebStorageStateStore, type User } from 'oidc-client-ts';
import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { SessionTimeoutErrorBoundary } from '../boundaries/session-timeout-error-boundary';
import type { KeycloakAuthClient } from '../clients/keycloak';
import { getKeycloakAuthClient } from '../clients/keycloak';
import { SessionStoragePersistor } from '../clients/persistors/session-storage';
import { IDPHint } from '../constants';
import { isTokenExpired, parseTokenPayload } from '../internal/helpers/jwt';
import type { Account, AuthApiConfig, AuthClient, AuthClientState, AuthProviderContext, AuthState, AuthSwapProviderContext, SwapContext, UserProfile } from '../types';
export enum AuthProviderType {
  OIDC_CLIENT = 'OIDC_CLIENT',
  OIDC_CONTEXT = 'OIDC_CONTEXT',
  KEYCLOAK_JS = 'KEYCLOAK_JS',
}
interface Props {
  idpHint?: IDPHint;
  redirectUri?: string;
  authApiConfig: AuthApiConfig;
  onSessionLost?: () => void;
}
interface SwapProviderOptions {
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  clientId: string;
  isEnabled?: boolean;
}

/**
 * Subscribe to the auth state on an AuthClient
 * @param client AuthClient
 * @returns AuthState
 */
export function useAuthState(client) {
  const $ = _c(5);
  let t0;
  if ($[0] !== client) {
    t0 = () => client.getAuthState();
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [authState, setAuthState] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== client) {
    t1 = () => {
      const unsub = client.subscribe(newState => setAuthState(newState));
      return () => {
        unsub();
      };
    };
    t2 = [client];
    $[2] = client;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return authState;
}

/**
 * Get the user profile from the auth state on the auth client.
 * @param tokens The state from the AuthClient
 * @returns UserProfile
 */
export function useUserProfile(tokens) {
  const $ = _c(18);
  const {
    idToken,
    token
  } = tokens;
  let t0;
  bb0: {
    if (!token || !idToken) {
      t0 = undefined;
      break bb0;
    }
    let parsedIdToken;
    let parsedToken;
    let t1;
    if ($[0] !== idToken || $[1] !== token) {
      parsedToken = parseTokenPayload(token);
      let t2;
      if ($[5] !== idToken) {
        t2 = parseTokenPayload(idToken);
        $[5] = idToken;
        $[6] = t2;
      } else {
        t2 = $[6];
      }
      parsedIdToken = t2;
      t1 = parsedToken.scope?.split(" ") ?? [];
      $[0] = idToken;
      $[1] = token;
      $[2] = parsedIdToken;
      $[3] = parsedToken;
      $[4] = t1;
    } else {
      parsedIdToken = $[2];
      parsedToken = $[3];
      t1 = $[4];
    }
    const scopes = t1;
    let t2;
    if ($[7] !== parsedToken.newfront_accounts) {
      t2 = parsedToken.newfront_accounts ?? [];
      $[7] = parsedToken.newfront_accounts;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    const t3 = parsedToken.accountUuids?.[0];
    let t4;
    if ($[9] !== parsedIdToken.email || $[10] !== parsedIdToken.family_name || $[11] !== parsedIdToken.given_name || $[12] !== parsedIdToken.profilePic || $[13] !== parsedIdToken.userId || $[14] !== scopes || $[15] !== t2 || $[16] !== t3) {
      t4 = {
        uuid: parsedIdToken.userId,
        email: parsedIdToken.email,
        profilePic: parsedIdToken.profilePic,
        roles: scopes,
        scopes,
        firstName: parsedIdToken.given_name,
        lastName: parsedIdToken.family_name,
        accounts: t2,
        accountUuid: t3
      };
      $[9] = parsedIdToken.email;
      $[10] = parsedIdToken.family_name;
      $[11] = parsedIdToken.given_name;
      $[12] = parsedIdToken.profilePic;
      $[13] = parsedIdToken.userId;
      $[14] = scopes;
      $[15] = t2;
      $[16] = t3;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t0 = t4;
  }
  return t0;
}

/**
 * Login using the client.
 * @param client AuthClient
 */
export function useAuthClientLogin(client) {
  const $ = _c(9);
  const [error, setError] = useState();
  const {
    isReady,
    isLoggedIn
  } = useAuthState(client);
  let t0;
  let t1;
  if ($[0] !== client || $[1] !== isReady) {
    t0 = () => {
      if (!isReady) {
        client.start().catch(e => {
          setError(e);
        });
      }
    };
    t1 = [isReady, client];
    $[0] = client;
    $[1] = isReady;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== client || $[5] !== isLoggedIn || $[6] !== isReady) {
    t2 = () => {
      if (isReady && !isLoggedIn) {
        client.login().catch(e_0 => {
          setError(e_0);
        });
      }
    };
    t3 = [isLoggedIn, isReady, client];
    $[4] = client;
    $[5] = isLoggedIn;
    $[6] = isReady;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  if (error) {
    throw error;
  }
}

/**
 * Returns a function that can be used to get an updated auth token that can be used
 * to make requests. This should be called before every request.
 * @param client AuthClient
 * @returns
 */
export function useAccessTokenCallback(client) {
  const $ = _c(2);
  let t0;
  if ($[0] !== client) {
    t0 = async () => {
      const tokens = await client.updateTokens();
      return tokens?.token;
    };
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * Returns an async callback that will swap the current token for a another token
 * for a different client id.
 */
function useSwapTokenProvider(authClient: AuthClient, clientId: string, isEnabled = true, initialSwapContext: SwapContext | undefined = undefined): AuthSwapProviderContext {
  const authState = useAuthState(authClient);
  const [token, setToken] = useState<string>();
  const [scopes, setScopes] = useState<string[]>();
  const [swapContext, setSwapContext] = useState<SwapContext | undefined>(initialSwapContext);
  const userDetails = useUserProfile({
    idToken: authState.idToken,
    token
  });

  // Use refs to store mutable state that shouldn't trigger rerenders
  const stateRef = useRef({
    token,
    swapContext,
    pendingSwap: undefined as Promise<string | undefined> | undefined,
    isSwapping: false
  });

  // Keep ref up to date without triggering rerenders
  useEffect(() => {
    stateRef.current.token = token;
    stateRef.current.swapContext = swapContext;
  }, [token, swapContext]);

  // Create a stable request swap token function that always uses latest state
  const requestSwapToken = useCallback(async () => {
    const state = stateRef.current;

    // Check auth state at execution time
    const currentAuthState = authClient.getAuthState();
    if (!currentAuthState.isLoggedIn || !isEnabled || !state.swapContext?.newfrontAccountUuid) {
      browserLogger.info('Skipping token swap - prerequisites not met', {
        isLoggedIn: currentAuthState.isLoggedIn,
        isEnabled,
        hasContext: !!state.swapContext?.newfrontAccountUuid
      });
      return;
    }

    // If already swapping, wait for the existing swap to complete
    if (state.isSwapping && state.pendingSwap) {
      browserLogger.info('Swap already in progress, waiting for existing swap');
      return state.pendingSwap;
    }
    try {
      state.isSwapping = true;
      const {
        token: newToken
      } = await authClient.swap(clientId, state.swapContext);
      setToken(newToken);
      return newToken;
    } catch (error) {
      browserLogger.error('Token swap failed:', {
        error
      });
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('403') || error.message.includes('invalid_token') || error.message.includes('Token invalid')) {
          setToken(undefined);
        }
      }
      throw error;
    } finally {
      state.isSwapping = false;
      state.pendingSwap = undefined;
    }
  }, [authClient, clientId, isEnabled]); // Only depend on stable values

  // Create a stable getToken function that always uses latest state
  const getSwappedAccessToken = useCallback(async () => {
    const state_0 = stateRef.current;

    // If there's no context, return undefined immediately
    if (!state_0.swapContext) {
      return undefined;
    }

    // If there's a pending swap, wait for it
    if (state_0.pendingSwap) {
      return state_0.pendingSwap;
    }

    // If we have a valid token, return it
    if (state_0.token && !isTokenExpired(state_0.token)) {
      return state_0.token;
    }

    // Start new swap request
    try {
      state_0.pendingSwap = requestSwapToken();
      const result = await state_0.pendingSwap;
      return result;
    } catch (error_0) {
      state_0.pendingSwap = undefined;
      throw error_0;
    }
  }, [requestSwapToken]); // Only depend on requestSwapToken

  // Wrap setSwapContext to start the token swap synchronously
  // This ensures pendingSwap is set before any queries run after re-render
  const setSwapContextAndSwap: typeof setSwapContext = useCallback(newContextOrUpdater => {
    // Handle both direct value and updater function forms
    const newContext = typeof newContextOrUpdater === 'function' ? newContextOrUpdater(stateRef.current.swapContext) : newContextOrUpdater;
    if (newContext?.newfrontAccountUuid) {
      const currentAuthState_0 = authClient.getAuthState();
      if (currentAuthState_0.isLoggedIn && isEnabled) {
        // Clear token and start swap BEFORE triggering re-render
        stateRef.current.token = undefined;
        stateRef.current.swapContext = newContext;

        // Start the swap immediately and store the promise
        // This must happen before setSwapContext triggers re-renders
        if (!stateRef.current.isSwapping) {
          stateRef.current.isSwapping = true;
          stateRef.current.pendingSwap = authClient.swap(clientId, newContext).then(({
            token: newToken_0
          }) => {
            setToken(newToken_0);
            stateRef.current.isSwapping = false;
            stateRef.current.pendingSwap = undefined;
            return newToken_0;
          }, error_1 => {
            browserLogger.error('Token swap failed:', {
              error: error_1
            });
            stateRef.current.isSwapping = false;
            stateRef.current.pendingSwap = undefined;
            if (error_1 instanceof Error) {
              if (error_1.message.includes('401') || error_1.message.includes('403') || error_1.message.includes('invalid_token') || error_1.message.includes('Token invalid')) {
                setToken(undefined);
              }
            }
            throw error_1;
          });
        }
        setScopes(undefined);
        setToken(undefined);
      }
    }
    setSwapContext(newContext);
  }, [authClient, clientId, isEnabled, setSwapContext]);

  // Effect to handle initial swap context (e.g., from URL on page load)
  useEffect(() => {
    if (!swapContext?.newfrontAccountUuid) return;
    // Skip if swap already in progress (started by setSwapContextAndSwap)
    if (stateRef.current.pendingSwap) return;
    const currentAuthState_1 = authClient.getAuthState();
    if (!currentAuthState_1.isLoggedIn) {
      browserLogger.info('Not logged in, skipping token swap');
      return;
    }
    setScopes(undefined);
    setToken(undefined);

    // Store the pending swap promise so getSwappedAccessToken can await it
    stateRef.current.pendingSwap = requestSwapToken();
  }, [swapContext, authClient, requestSwapToken]);
  return {
    getSwappedAccessToken,
    getToken: getSwappedAccessToken,
    scopes,
    swapContext,
    setSwapContext: setSwapContextAndSwap,
    userDetails
  };
}

/**
 * Create the KeycloakAuthClient that will be used to manage the JWT token.
 */
function useKeycloakAuthClient(options) {
  const $ = _c(6);
  const {
    authApiConfig,
    idpHint,
    redirectUri
  } = options;
  const {
    clientId,
    realm,
    url
  } = authApiConfig;
  let t0;
  let t1;
  if ($[0] !== clientId || $[1] !== idpHint || $[2] !== realm || $[3] !== redirectUri || $[4] !== url) {
    t1 = getKeycloakAuthClient({
      config: {
        clientId,
        realm,
        url
      },
      initOptions: {
        pkceMethod: "S256"
      },
      loginOptions: {
        idpHint,
        redirectUri
      },
      tokenPersistor: new SessionStoragePersistor(`kcToken-${realm}-${clientId}`, window.localStorage)
    });
    $[0] = clientId;
    $[1] = idpHint;
    $[2] = realm;
    $[3] = redirectUri;
    $[4] = url;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  t0 = t1;
  return t0;
}
export interface AuthProviderProps {
  providerType?: AuthProviderType;
  children?: ReactNode;
}
export const createAuthProvider = (props: Props): Provider<AuthProviderContext, AuthProviderProps> => {
  const {
    authApiConfig
  } = props;
  const {
    clientId,
    realm
  } = authApiConfig;
  return createProvider<AuthProviderContext, AuthProviderProps>(({
    providerType
  }) => {
    if (providerType === AuthProviderType.OIDC_CLIENT) {
      return useOIDCAuthProvider();
    }
    if (providerType === AuthProviderType.OIDC_CONTEXT) {
      return useOIDCAuthContext();
    }
    const client = useKeycloakAuthClient(props);
    const authState = useAuthState(client);
    const userDetails = useUserProfile(authState);
    const logout = useCallback(async (redirectUrl: string) => client.logout(redirectUrl), [client]);
    const getAccessTokenCallback = useAccessTokenCallback(client);
    return {
      getAccessTokenCallback,
      getToken: getAccessTokenCallback,
      logout,
      userDetails,
      authState,
      client
    } as AuthProviderContext;
  }, {
    name: `AuthProvider-${realm}-${clientId}`,
    wrapper: ({
      children,
      providerType
    }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const router = useRouter();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const localStateStore = useMemo(() => {
        return new WebStorageStateStore({
          store: window.localStorage
        });
      }, []);
      if (providerType === AuthProviderType.OIDC_CLIENT) {
        const configuration: OidcConfiguration = {
          client_id: authApiConfig.clientId,
          redirect_uri: `${window.location.origin}/#authentication/callback`,
          silent_redirect_uri: `${window.location.origin}/#authentication/silent-callback`,
          monitor_session: true,
          scope: 'openid profile email offline_access',
          authority: `${authApiConfig.url}/realms/${authApiConfig.realm}`,
          refresh_time_before_tokens_expiration_in_second: 30,
          storage: localStorage,
          extras: props.idpHint === IDPHint.GSUITE ? {
            kc_idp_hint: 'gsuite'
          } : {}
        };
        const withCustomHistory = () => {
          return {
            replaceState: (url: string) => {
              router.replace({
                pathname: url
              }).then(() => {
                window.dispatchEvent(new Event('popstate'));
              });
            }
          };
        };
        return <OidcProvider onEvent={(_config, eventName, data) => {
          if (eventName.includes('error')) {
            console.error('[Newfront Auth Error]', eventName, data);
          }
        }} withCustomHistory={withCustomHistory} configuration={configuration} onSessionLost={() => {
          console.error('[Newfront Auth Error]', 'Session lost');
        }} authenticatingComponent={() => null} authenticatingErrorComponent={() => null} loadingComponent={() => null} callbackSuccessComponent={() => null} sessionLostComponent={() => {
          return <SessionTimeoutErrorBoundary />;
        }}>
              <OidcSecure>{children}</OidcSecure>
            </OidcProvider>;
      }
      if (providerType === AuthProviderType.OIDC_CONTEXT) {
        return <AuthProvider authority={`${authApiConfig.url}/realms/${authApiConfig.realm}`} client_id={authApiConfig.clientId} redirect_uri={`${window.location.href}`} automaticSilentRenew userStore={localStateStore}>
              {children}
            </AuthProvider>;
      }
      return children as JSX.Element;
    }
  });
};
export interface AuthSwapProviderOptions {
  initialSwapContext?: SwapContext;
}
export function createAuthSwapProvider(options: SwapProviderOptions): Provider<AuthSwapProviderContext, AuthSwapProviderOptions> {
  const {
    authProvider,
    clientId,
    isEnabled = true
  } = options;
  return createProvider((opts: AuthSwapProviderOptions) => {
    const {
      initialSwapContext
    } = opts;
    const {
      client
    } = useProvider(authProvider);
    return useSwapTokenProvider(client, clientId, isEnabled, initialSwapContext);
  });
}
function getOidcAuthState(isLoading: boolean, isAuthenticated: boolean, user: User | null | undefined): AuthState {
  if (isLoading) {
    return {
      state: 'NOT_READY',
      isReady: false,
      isLoggedIn: false,
      idToken: undefined,
      token: undefined
    };
  }
  if (!isAuthenticated) {
    return {
      state: 'NOT_LOGGED_IN',
      isReady: true,
      isLoggedIn: false,
      idToken: undefined,
      token: undefined
    };
  }
  return {
    state: 'LOGGED_IN',
    isReady: true,
    isLoggedIn: true,
    idToken: user?.id_token ?? '',
    token: user?.access_token ?? '',
    error: undefined
  };
}
export function useOIDCAuthContext(): AuthProviderContext {
  const {
    user,
    isAuthenticated,
    isLoading,
    signinPopup,
    signinSilent,
    signoutRedirect,
    signinRedirect,
    removeUser
  } = useAuth();

  // Store latest user in a ref so long‑lived/cached consumers of getToken read the current user/token, not a stale closure
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  const client: AuthClient = useMemo(() => {
    return {
      login: async (redirectUri: string) => {
        signinSilent({
          redirect_uri: redirectUri,
          extraQueryParams: {
            kc_idp_hint: 'gsuite'
          }
        });
      },
      loginPopup: async (redirectUri_0: string) => {
        if (!isAuthenticated) {
          // Auth and Refresh Token expired. Must go through a clean login flow.
          // Otherwise, subsequent `login` call will be made with a stale Refresh Token, thus erroring.
          await removeUser();
        }
        signinPopup({
          redirect_uri: redirectUri_0,
          extraQueryParams: {
            kc_idp_hint: 'gsuite'
          }
        });
      },
      loginRedirect: async (redirectUri_1: string) => {
        signinRedirect({
          redirect_uri: redirectUri_1,
          extraQueryParams: {
            kc_idp_hint: 'gsuite'
          }
        });
      },
      // Not necessary when using OIDC
      start: async () => {
        return true;
      },
      // Not necessary when using OIDC
      stop: () => {
        return true;
      },
      // Not necessary when using OIDC
      subscribe: () => {
        return () => {
          return true;
        };
      },
      // Not necessary when using OIDC
      updateTokens: async () => {
        const newUser = await signinSilent();
        return {
          idToken: newUser?.id_token ?? '',
          token: newUser?.access_token ?? '',
          error: undefined
        };
      },
      getAuthState: () => getOidcAuthState(isLoading, isAuthenticated, userRef.current),
      logout: async (_redirectUri: string) => {
        await signoutRedirect();
      },
      swap: async () => {
        return {
          token: '',
          refreshToken: ''
        };
      },
      getDirectGrantLoginToken: async () => {
        return undefined;
      },
      getDirectGrantLoginTokenWithTotp: async () => {
        return undefined;
      },
      authenticateWithMagicLink: async () => {
        return '';
      },
      requestMagicLink: async () => {
        return false;
      }
    };
  }, [signinPopup, signinSilent, signoutRedirect, isAuthenticated, signinRedirect, user, isLoading]);
  return {
    authState: getOidcAuthState(isLoading, isAuthenticated, userRef.current),
    userDetails: {
      accounts: [],
      email: user?.profile.email as string,
      firstName: user?.profile.given_name as string,
      lastName: user?.profile.family_name as string,
      roles: user?.profile.roles as string[],
      scopes: user?.scope?.split(' ') ?? [],
      uuid: user?.profile.userId as string,
      profilePic: user?.profile.picture as string
    },
    getAccessTokenCallback: async () => userRef.current?.access_token ?? '',
    client,
    getToken: async () => {
      return userRef.current?.access_token;
    },
    logout: async () => {
      await signoutRedirect();
    }
  };
}
const useOIDCAuthProvider = () => {
  const $ = _c(36);
  const {
    accessToken,
    accessTokenPayload
  } = useOidcAccessToken();
  const {
    idToken,
    idTokenPayload
  } = useOidcIdToken();
  const {
    logout,
    login
  } = useOidc();
  const authState = useOIDCAuthStateFromUser();
  let t0;
  let t1;
  if ($[0] !== accessToken) {
    t0 = () => accessToken;
    t1 = () => accessToken;
    $[0] = accessToken;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = idTokenPayload.email as string;
  const t4 = idTokenPayload.given_name as string;
  const t5 = idTokenPayload.family_name as string;
  let t6;
  if ($[4] !== accessTokenPayload.scope) {
    t6 = accessTokenPayload?.scope?.split(" ") ?? [];
    $[4] = accessTokenPayload.scope;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== accessTokenPayload.scope) {
    t7 = accessTokenPayload?.scope?.split(" ") ?? [];
    $[6] = accessTokenPayload.scope;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = idTokenPayload?.profilePic;
  let t9;
  if ($[8] !== accessTokenPayload.userId || $[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = {
      accounts: t2,
      email: t3,
      firstName: t4,
      lastName: t5,
      roles: t6,
      scopes: t7,
      uuid: accessTokenPayload.userId,
      profilePic: t8
    };
    $[8] = accessTokenPayload.userId;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== logout) {
    t10 = async redirectUri => {
      await logout(redirectUri);
    };
    $[16] = logout;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== login) {
    t11 = async () => {
      await login();
    };
    $[18] = login;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== accessToken || $[21] !== idToken) {
    t12 = () => ({
      state: "LOGGED_IN",
      isLoggedIn: true,
      isReady: true,
      idToken,
      token: accessToken
    });
    $[20] = accessToken;
    $[21] = idToken;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== logout) {
    t13 = async redirectUri_0 => {
      await logout(redirectUri_0);
    };
    $[23] = logout;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = {
      login: t11,
      start: _temp,
      stop: _temp2,
      subscribe: _temp4,
      updateTokens: _temp5,
      getAuthState: t12,
      logout: t13,
      swap: _temp6,
      getDirectGrantLoginToken: _temp7,
      getDirectGrantLoginTokenWithTotp: _temp8,
      authenticateWithMagicLink: _temp9,
      requestMagicLink: _temp10
    };
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== authState || $[30] !== t0 || $[31] !== t1 || $[32] !== t10 || $[33] !== t14 || $[34] !== t9) {
    t15 = {
      getAccessTokenCallback: t0,
      getToken: t1,
      userDetails: t9,
      authState,
      logout: t10,
      client: t14
    };
    $[29] = authState;
    $[30] = t0;
    $[31] = t1;
    $[32] = t10;
    $[33] = t14;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};
export class SessionTimeoutError extends Error {}
function useOIDCAuthStateFromUser() {
  const $ = _c(6);
  const {
    oidcUserLoadingState
  } = useOidcUser();
  const {
    accessToken
  } = useOidcAccessToken();
  const {
    idToken
  } = useOidcIdToken();
  if (oidcUserLoadingState === OidcUserStatus.Loaded) {
    let t0;
    if ($[0] !== accessToken || $[1] !== idToken) {
      t0 = {
        state: "LOGGED_IN",
        idToken,
        isLoggedIn: true,
        token: accessToken,
        isReady: true
      };
      $[0] = accessToken;
      $[1] = idToken;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== accessToken || $[4] !== idToken) {
    t0 = {
      state: "NOT_READY",
      idToken,
      isLoggedIn: false,
      isReady: false,
      token: accessToken
    };
    $[3] = accessToken;
    $[4] = idToken;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}
async function _temp() {
  return true;
}
function _temp2() {
  return true;
}
function _temp3() {
  return true;
}
function _temp4() {
  return _temp3;
}
async function _temp5() {
  return {
    idToken: "",
    token: ""
  };
}
async function _temp6() {
  return {
    token: "",
    refreshToken: ""
  };
}
async function _temp7() {}
async function _temp8() {}
async function _temp9() {
  return "";
}
async function _temp10() {
  return false;
}